import { useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useFormat } from "../i18n/format";
import { describeError, useToast } from "../components/toast";
import type { ActionKey } from "../components/toast/ToastProvider";
import type { PremiseCommit } from "../context/PremiseWorkspace";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  Stack,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BlockIcon from "@mui/icons-material/Block";
import ContentCopyIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PrintIcon from "@mui/icons-material/PrintOutlined";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import QRCode from "react-qr-code";
import type { GuestWebConfig, PremiseConfig } from "@shared/types/foyer";
import {
  foyerApi,
  type GuestAnalyticsSummary,
  type GuestEmbedUrls,
  type GuestTokensResponse,
} from "../http/foyer";
import { PageHeader } from "../components/PageHeader";
import { SegmentedChoice } from "../components/startnav/StartNavLayout";
import { tokens } from "../theme";
import { PermissionGate } from "../auth/access";

const DEFAULT_CONFIG: GuestWebConfig = {
  enabled: false,
  wifi: { visible: false },
};

/** Rooms shown before "Show more rooms". */
const PAGE = 10;

type Toast = { msg: string; severity: "success" | "error" };
type SortKey = "room" | "status" | "created";

/**
 * Guest web (NendaPortal_1 "Gästwebb" 1800:26866): the phone companion a guest
 * reaches from a room QR code or the hotel's Wi-Fi page.
 *
 * The three Yes/No choices save the moment they are picked and toast. The embed
 * key and the room codes are server actions and take effect at once too.
 */
export default function GuestCompanionPage(props: {
  premise: PremiseConfig;
  /** Saves a change to the premise and toasts it (PremiseWorkspace `commit`). */
  onCommit: PremiseCommit;
}) {
  const { premise, onCommit } = props;
  const premiseId = premise.premiseId;
  const where = premise.premiseName;

  const config: GuestWebConfig = { ...DEFAULT_CONFIG, ...premise.guestWeb };
  /** Change one part of the guest web config, applied to the SAVED config. */
  const setConfig = (
    patch: (current: GuestWebConfig) => GuestWebConfig,
    success: string,
    failure: ActionKey,
    failureParams?: Record<string, string | number>
  ) =>
    void onCommit(
      (c) => ({
        ...c,
        guestWeb: patch({ ...DEFAULT_CONFIG, ...c.guestWeb }),
      }),
      { success, failure, failureParams }
    );

  const [tokensRes, setTokens] = useState<GuestTokensResponse | null>(null);
  const [analytics, setAnalytics] = useState<GuestAnalyticsSummary | null>(
    null
  );
  const [embed, setEmbed] = useState<GuestEmbedUrls | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const { t } = useTranslation(["offers", "common"]);
  const fmt = useFormat();
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<{ key: SortKey; asc: boolean }>({
    key: "room",
    asc: true,
  });
  const [shown, setShown] = useState(PAGE);
  const [managing, setManaging] = useState<string | null>(null);

  const reload = async () => {
    const [tk, an, gw] = await Promise.all([
      foyerApi.listGuestTokens(premiseId),
      foyerApi.guestAnalytics(premiseId, 30).catch(() => null),
      // Server-built: the embed URLs need the guest surface's hostname.
      foyerApi.getGuestWeb(premiseId).catch(() => null),
    ]);
    setTokens(tk);
    setAnalytics(an);
    setEmbed(gw?.embed ?? null);
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    reload()
      .catch((e) => toast.error(describeError(t, "loadGuestWeb", e)))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [premiseId]);

  const run = async (
    fn: () => Promise<void>,
    ok: string | null,
    fail: ActionKey
  ) => {
    setBusy(true);
    try {
      await fn();
      if (ok) toast.success(ok);
    } catch (e) {
      toast.error(describeError(t, fail, e));
    } finally {
      setBusy(false);
    }
  };

  const generateAll = (regenerate: boolean) => {
    if (
      regenerate &&
      !window.confirm(t("guestCompanionPage.confirmRegenerateAll"))
    )
      return;
    return run(
      async () => {
        const res = await foyerApi.bulkIssueGuestTokens(premiseId, {
          regenerate,
        });
        await reload();
        toast.success(
          regenerate
            ? t("guestCompanionPage.generatedCodes", {
                count: res.count,
                where,
              })
            : t("guestCompanionPage.createdMissingCodes", {
                count: res.count,
                where,
              })
        );
      },
      null,
      "generateUnitCodes"
    );
  };

  const mintEmbedKey = (rotating: boolean) => {
    if (rotating && !window.confirm(t("guestCompanionPage.confirmRotate")))
      return;
    return run(
      async () => setEmbed((await foyerApi.mintGuestEmbedKey(premiseId)).embed),
      rotating
        ? t("guestCompanionPage.linkRotated")
        : t("guestCompanionPage.linkCreated"),
      rotating ? "rotateWifiPageLink" : "createWifiPageLink"
    );
  };

  const removeEmbedKey = () => {
    if (!window.confirm(t("guestCompanionPage.confirmRemoveLink"))) return;
    return run(
      async () => {
        await foyerApi.deleteGuestEmbedKey(premiseId);
        setEmbed(null);
      },
      t("guestCompanionPage.linkRemoved"),
      "removeWifiPageLink"
    );
  };

  const copy = async (value: string, what: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(t("guestCompanionPage.copied", { what }));
    } catch {
      toast.error(t("guestCompanionPage.copyFailed", { what }));
    }
  };

  const rooms = useMemo(() => tokensRes?.rooms ?? [], [tokensRes]);
  const activeByRoom = useMemo(
    () => tokensRes?.activeByRoom ?? {},
    [tokensRes]
  );
  const activeCount = rooms.filter((r) => activeByRoom[r.id]).length;

  const visibleRooms = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? rooms.filter((r) => r.label.toLowerCase().includes(q))
      : rooms;
    const dir = sort.asc ? 1 : -1;
    const created = (id: string) => activeByRoom[id]?.createdAt ?? "";
    return [...filtered].sort((a, b) => {
      if (sort.key === "status") {
        const d = Number(!!activeByRoom[b.id]) - Number(!!activeByRoom[a.id]);
        if (d) return d * dir;
      } else if (sort.key === "created") {
        const d = created(a.id).localeCompare(created(b.id));
        if (d) return d * dir;
      }
      return a.label.localeCompare(b.label, undefined, { numeric: true }) * dir;
    });
  }, [rooms, activeByRoom, query, sort]);

  if (loading) return <LinearProgress />;

  const sortBy = (key: SortKey) =>
    setSort((s) => ({ key, asc: s.key === key ? !s.asc : true }));

  return (
    <Box sx={{ pb: 6, maxWidth: 1120 }} data-testid="guest-web-page">
      <PageHeader
        title={t("guestCompanionPage.title")}
        subtitle={t("guestCompanionPage.subtitle")}
      />

      <SectionTitle
        title={t("guestCompanionPage.activation.title")}
        subtitle={t("guestCompanionPage.activation.subtitle")}
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          columnGap: 6,
          rowGap: 3,
          mb: 5,
        }}
      >
        <PermissionGate
          resource="guest-web"
          level="edit"
          deniedProps={{ disabled: true }}
        >
          <ChoiceRow
            title={t("guestCompanionPage.enable.title")}
            help={t("guestCompanionPage.enable.help")}
            value={config.enabled}
            onChange={(enabled) =>
              setConfig(
                (g) => ({ ...g, enabled }),
                enabled
                  ? t("guestCompanionPage.enable.on", { where })
                  : t("guestCompanionPage.enable.off", { where }),
                enabled ? "turnOnGuestWebFor" : "turnOffGuestWebFor",
                { name: where }
              )
            }
            testId="guest-enable"
          />
        </PermissionGate>
        <PermissionGate
          resource="guest-web"
          level="edit"
          deniedProps={{ disabled: true }}
        >
          <ChoiceRow
            title={t("guestCompanionPage.wifi.title")}
            help={t("guestCompanionPage.wifi.help")}
            value={!!config.wifi?.visible}
            onChange={(visible) =>
              setConfig(
                (g) => ({ ...g, wifi: { ...g.wifi, visible } }),
                visible
                  ? t("guestCompanionPage.wifi.shown", { where })
                  : t("guestCompanionPage.wifi.hidden", { where }),
                visible ? "showGuestWebWifi" : "hideGuestWebWifi"
              )
            }
            testId="guest-wifi-visible"
          />
        </PermissionGate>
      </Box>

      {/* The hotel's "you're connected" page: an image of the QR and a link. */}
      <SubTitle
        title={t("guestCompanionPage.wifiPage.title")}
        subtitle={t("guestCompanionPage.wifiPage.subtitle")}
      />
      {!embed ? (
        <PermissionGate
          resource="guest-web"
          level="edit"
          deniedProps={{ disabled: true }}
        >
          <Button
            variant="outlined"
            startIcon={<QrCode2Icon />}
            disabled={busy}
            onClick={() => mintEmbedKey(false)}
            sx={{ mb: 5, textTransform: "none" }}
            data-testid="guest-embed-create"
          >
            {t("guestCompanionPage.wifiPage.create")}
          </Button>
        </PermissionGate>
      ) : (
        <>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ sm: "center" }}
            sx={{ mb: 4 }}
          >
            {/* A live fetch of the real endpoint: if this renders, the hotel's
                page will render too. */}
            <Box
              component="img"
              src={`${embed.imageUrl}?size=320`}
              alt={t("guestCompanionPage.wifiPage.qrAlt")}
              sx={{ width: 104, height: 104, flexShrink: 0, bgcolor: "#fff" }}
            />
            <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
              <UrlRow
                label={t("guestCompanionPage.wifiPage.imageUrl")}
                value={embed.imageUrl}
                onCopy={() =>
                  copy(
                    embed.imageUrl,
                    t("guestCompanionPage.wifiPage.imageUrlName")
                  )
                }
              />
              <UrlRow
                label={t("guestCompanionPage.wifiPage.linkUrl")}
                value={embed.linkUrl}
                underline
                onCopy={() =>
                  copy(
                    embed.linkUrl,
                    t("guestCompanionPage.wifiPage.linkUrlName")
                  )
                }
              />
            </Stack>
          </Stack>

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={3}
            alignItems={{ md: "center" }}
            sx={{ mb: 5 }}
          >
            <Box sx={{ maxWidth: 460 }}>
              <PermissionGate
                resource="guest-web"
                level="edit"
                deniedProps={{ disabled: true }}
              >
                <ChoiceRow
                  title={t("guestCompanionPage.roomCodes.title")}
                  help={t("guestCompanionPage.roomCodes.help")}
                  value={!!config.embedRoomQr}
                  onChange={(embedRoomQr) =>
                    setConfig(
                      (g) => ({ ...g, embedRoomQr }),
                      embedRoomQr
                        ? t("guestCompanionPage.roomCodes.allowed", { where })
                        : t("guestCompanionPage.roomCodes.notAllowed", {
                            where,
                          }),
                      "changeWifiPageRoomCodes"
                    )
                  }
                  testId="guest-embed-room"
                />
              </PermissionGate>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <PermissionGate
                resource="guest-web"
                level="edit"
                deniedProps={{ disabled: true }}
              >
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  disabled={busy}
                  onClick={() => mintEmbedKey(true)}
                  sx={{ minWidth: 140, textTransform: "none" }}
                  data-testid="guest-embed-rotate"
                >
                  {t("guestCompanionPage.wifiPage.rotate")}
                </Button>
              </PermissionGate>
              <Tooltip title={t("guestCompanionPage.wifiPage.remove")}>
                <PermissionGate
                  resource="guest-web"
                  level="edit"
                  deniedProps={{ disabled: true }}
                >
                  <IconButton
                    aria-label={t("guestCompanionPage.wifiPage.remove")}
                    disabled={busy}
                    onClick={removeEmbedKey}
                    data-testid="guest-embed-remove"
                  >
                    <DeleteIcon />
                  </IconButton>
                </PermissionGate>
              </Tooltip>
            </Stack>
          </Stack>
        </>
      )}

      <SubTitle
        title={t("guestCompanionPage.perUnit.title")}
        subtitle={t("guestCompanionPage.perUnit.subtitle", {
          active: activeCount,
          total: rooms.length,
        })}
      />
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems={{ md: "center" }}
        sx={{ mb: 2 }}
      >
        <TextField
          size="small"
          placeholder={t("guestCompanionPage.perUnit.searchPlaceholder")}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setShown(PAGE);
          }}
          sx={{ flex: 1, maxWidth: { md: 280 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          data-testid="guest-room-search"
        />
        <Box sx={{ flex: 1 }} />
        <PermissionGate
          resource="guest-web"
          level="edit"
          deniedProps={{ disabled: true }}
        >
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            disabled={busy}
            onClick={() => generateAll(false)}
            sx={{ textTransform: "none" }}
            data-testid="guest-generate-missing"
          >
            {t("guestCompanionPage.perUnit.generateMissing")}
          </Button>
        </PermissionGate>
        <PermissionGate
          resource="guest-web"
          level="edit"
          deniedProps={{ disabled: true }}
        >
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            disabled={busy}
            onClick={() => generateAll(true)}
            sx={{ textTransform: "none" }}
            data-testid="guest-generate-all"
          >
            {t("guestCompanionPage.perUnit.generateAll")}
          </Button>
        </PermissionGate>
        <PermissionGate
          resource="guest-web"
          level="edit"
          deniedProps={{ disabled: true }}
        >
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            component="a"
            href={foyerApi.guestQrPdfUrl(premiseId)}
            target="_blank"
            rel="noreferrer"
            sx={{ textTransform: "none" }}
          >
            {t("guestCompanionPage.perUnit.printPdf")}
          </Button>
        </PermissionGate>
      </Stack>

      <Box
        sx={{
          border: "1px solid",
          borderColor: tokens.divider,
          borderRadius: "12px",
          bgcolor: tokens.surface,
          overflow: "hidden",
        }}
        data-testid="guest-room-table"
      >
        <Box
          sx={{
            ...ROW_GRID,
            minHeight: 48,
            borderBottom: "1px solid",
            borderColor: tokens.divider,
            typography: "bodySm",
            color: tokens.inkMuted,
          }}
        >
          <HeaderCell
            label={t("guestCompanionPage.perUnit.columns.unit")}
            k="room"
            sort={sort}
            onSort={sortBy}
          />
          <HeaderCell
            label={t("guestCompanionPage.perUnit.columns.status")}
            k="status"
            sort={sort}
            onSort={sortBy}
          />
          <HeaderCell
            label={t("guestCompanionPage.perUnit.columns.created")}
            k="created"
            sort={sort}
            onSort={sortBy}
          />
          <Box>{t("guestCompanionPage.perUnit.columns.qrCode")}</Box>
        </Box>
        {visibleRooms.length === 0 && (
          <Typography variant="body2" sx={{ p: 2, color: tokens.inkMuted }}>
            {rooms.length === 0
              ? t("guestCompanionPage.perUnit.noUnits")
              : t("guestCompanionPage.perUnit.noMatch")}
          </Typography>
        )}
        {visibleRooms.slice(0, shown).map((room) => {
          const active = activeByRoom[room.id];
          return (
            <Box
              key={room.id}
              sx={{
                ...ROW_GRID,
                minHeight: 48,
                borderBottom: "1px solid",
                borderColor: tokens.divider,
                typography: "body2",
              }}
              data-testid={`guest-room-${room.id}`}
            >
              <Box sx={{ fontWeight: "fontWeightSemibold" }}>{room.label}</Box>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: active ? tokens.success : tokens.warning,
                  }}
                />
                <Box
                  sx={{
                    fontWeight: "fontWeightSemibold",
                    color: active ? tokens.success : tokens.warning,
                  }}
                >
                  {active
                    ? t("guestCompanionPage.perUnit.active")
                    : t("guestCompanionPage.perUnit.missing")}
                </Box>
              </Stack>
              <Box>
                {active
                  ? fmt.dateTime(active.createdAt)
                  : t("common:labels.noValue")}
              </Box>
              <Box>
                {active ? (
                  <PermissionGate
                    resource="guest-web"
                    level="edit"
                    deniedProps={{ disabled: true }}
                  >
                    <Button
                      size="small"
                      endIcon={<ArrowForwardIcon fontSize="small" />}
                      onClick={() => setManaging(room.id)}
                      sx={{ textTransform: "none", px: 0 }}
                      data-testid={`guest-room-manage-${room.id}`}
                    >
                      {t("guestCompanionPage.perUnit.manage")}
                    </Button>
                  </PermissionGate>
                ) : (
                  <PermissionGate
                    resource="guest-web"
                    level="edit"
                    deniedProps={{ disabled: true }}
                  >
                    <Button
                      size="small"
                      startIcon={<AddCircleOutlineIcon fontSize="small" />}
                      disabled={busy}
                      onClick={() =>
                        run(
                          async () => {
                            await foyerApi.issueRoomToken(premiseId, room.id);
                            await reload();
                          },
                          t("guestCompanionPage.perUnit.codeCreated", {
                            room: room.label,
                          }),
                          "createRoomCode"
                        )
                      }
                      sx={{
                        textTransform: "none",
                        px: 0,
                        fontWeight: "fontWeightSemibold",
                      }}
                      data-testid={`guest-room-create-${room.id}`}
                    >
                      {t("guestCompanionPage.perUnit.create")}
                    </Button>
                  </PermissionGate>
                )}
              </Box>
            </Box>
          );
        })}
        {visibleRooms.length > 0 && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 2, py: 1.5, typography: "bodySm" }}
          >
            <Typography variant="bodySm" sx={{ color: tokens.inkMuted }}>
              <Trans
                t={t}
                i18nKey="guestCompanionPage.perUnit.showing"
                values={{
                  shown: Math.min(shown, visibleRooms.length),
                  total: visibleRooms.length,
                }}
                components={{
                  strong: <Box component="strong" sx={{ color: tokens.ink }} />,
                }}
              />
            </Typography>
            {shown < visibleRooms.length && (
              <Button
                size="small"
                endIcon={<ExpandMoreIcon />}
                onClick={() => setShown((n) => n + PAGE)}
                sx={{ textTransform: "none", color: tokens.ink }}
                data-testid="guest-room-more"
              >
                {t("guestCompanionPage.perUnit.showMore")}
              </Button>
            )}
          </Stack>
        )}
      </Box>

      {analytics && <Usage analytics={analytics} />}

      {managing && (
        <ManageRoomDialog
          premiseId={premiseId}
          room={rooms.find((r) => r.id === managing)!}
          tokenId={activeByRoom[managing]?.id}
          busy={busy}
          onClose={() => setManaging(null)}
          onChanged={reload}
          onToast={(next) =>
            next.severity === "error"
              ? toast.error(next.msg)
              : toast.success(next.msg)
          }
        />
      )}
    </Box>
  );
}

const ROW_GRID = {
  display: "grid",
  gridTemplateColumns: "1.2fr 1fr 1.2fr 1fr",
  alignItems: "center",
  gap: 2,
  px: 2,
} as const;

function SectionTitle({
  title,
  subtitle,
}: {
  title: string;
  subtitle: string;
}) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        component="h2"
        sx={{ typography: "titleLg", fontWeight: "fontWeightSemibold" }}
      >
        {title}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5, color: tokens.inkMuted }}>
        {subtitle}
      </Typography>
    </Box>
  );
}

function SubTitle({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography component="h3" sx={{ fontWeight: "fontWeightSemibold" }}>
        {title}
      </Typography>
      <Typography variant="bodySm" sx={{ mt: 0.25, color: tokens.inkMuted }}>
        {subtitle}
      </Typography>
    </Box>
  );
}

/** A setting with its explanation on the left and Yes / No on the right. */
function ChoiceRow({
  title,
  help,
  value,
  onChange,
  testId,
  disabled,
}: {
  title: string;
  help: string;
  value: boolean;
  onChange: (v: boolean) => void;
  testId: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation("common");
  return (
    <Stack direction="row" spacing={3} alignItems="flex-start">
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: "fontWeightSemibold" }}>
          {title}
        </Typography>
        <Typography variant="bodySm" sx={{ mt: 0.25, color: tokens.inkMuted }}>
          {help}
        </Typography>
      </Box>
      <Box sx={{ flexShrink: 0 }}>
        <SegmentedChoice
          value={value ? "yes" : "no"}
          options={[
            { value: "yes", label: t("buttons.yes") },
            { value: "no", label: t("buttons.no") },
          ]}
          onChange={(v) => onChange(v === "yes")}
          disabled={disabled}
          testId={testId}
        />
      </Box>
    </Stack>
  );
}

/** One copyable URL in a tinted strip. */
function UrlRow({
  label,
  value,
  underline,
  onCopy,
}: {
  label: string;
  value: string;
  underline?: boolean;
  onCopy: () => void;
}) {
  const { t } = useTranslation("common");
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2}
      sx={{ px: 1.5, py: 1, bgcolor: tokens.rowAlt, borderRadius: "6px" }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
          {label}
        </Typography>
        <Typography
          variant="bodySm"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textDecoration: underline ? "underline" : "none",
          }}
          title={value}
        >
          {value}
        </Typography>
      </Box>
      <Button
        startIcon={<ContentCopyIcon />}
        onClick={onCopy}
        sx={{
          textTransform: "none",
          fontWeight: "fontWeightSemibold",
          flexShrink: 0,
        }}
      >
        {t("buttons.copy")}
      </Button>
    </Stack>
  );
}

function HeaderCell({
  label,
  k,
  sort,
  onSort,
}: {
  label: string;
  k: SortKey;
  sort: { key: SortKey; asc: boolean };
  onSort: (k: SortKey) => void;
}) {
  return (
    <Box>
      <TableSortLabel
        active={sort.key === k}
        direction={sort.key === k && !sort.asc ? "desc" : "asc"}
        onClick={() => onSort(k)}
      >
        {label}
      </TableSortLabel>
    </Box>
  );
}

/** A room's code: shown as a QR, opened, replaced, or revoked. */
function ManageRoomDialog({
  premiseId,
  room,
  tokenId,
  busy,
  onClose,
  onChanged,
  onToast,
}: {
  premiseId: string;
  room: { id: string; label: string };
  tokenId?: string;
  busy: boolean;
  onClose: () => void;
  onChanged: () => Promise<void>;
  onToast: (t: Toast) => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const { t } = useTranslation(["offers", "common"]);

  useEffect(() => {
    let alive = true;
    foyerApi
      .revealRoomToken(premiseId, room.id)
      .then((res) => alive && setUrl(res.url))
      .catch(() => alive && setUrl(null));
    return () => {
      alive = false;
    };
  }, [premiseId, room.id]);

  const regenerate = async () => {
    if (
      !window.confirm(
        t("guestCompanionPage.manage.confirmReplace", { room: room.label })
      )
    )
      return;
    setWorking(true);
    try {
      const issued = await foyerApi.issueRoomToken(premiseId, room.id);
      setUrl(issued.url);
      await onChanged();
      onToast({
        msg: t("guestCompanionPage.manage.issued", { room: room.label }),
        severity: "success",
      });
    } catch (e) {
      onToast({
        msg: describeError(t, "replaceRoomCode", e, { room: room.label }),
        severity: "error",
      });
    } finally {
      setWorking(false);
    }
  };

  const revoke = async () => {
    if (!tokenId) return;
    setWorking(true);
    try {
      await foyerApi.revokeGuestToken(premiseId, tokenId);
      await onChanged();
      onToast({
        msg: t("guestCompanionPage.manage.revoked", { room: room.label }),
        severity: "success",
      });
      onClose();
    } catch (e) {
      onToast({
        msg: describeError(t, "revokeRoomCode", e, { room: room.label }),
        severity: "error",
      });
    } finally {
      setWorking(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {t("guestCompanionPage.manage.title", { room: room.label })}
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            minHeight: 200,
            alignItems: "center",
          }}
        >
          {url ? (
            <Box sx={{ bgcolor: "#fff", p: 1.5 }}>
              <QRCode value={url} size={180} />
            </Box>
          ) : (
            <Typography variant="bodySm" sx={{ color: tokens.inkMuted }}>
              {t("guestCompanionPage.manage.loading")}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
        <Stack direction="row" spacing={1}>
          {url && (
            <Button
              startIcon={<OpenInNewIcon />}
              component="a"
              href={url}
              target="_blank"
              rel="noreferrer"
            >
              {t("common:buttons.open")}
            </Button>
          )}
          <Button
            startIcon={<RefreshIcon />}
            disabled={busy || working}
            onClick={regenerate}
          >
            {t("guestCompanionPage.manage.newCode")}
          </Button>
          <Button
            color="error"
            startIcon={<BlockIcon />}
            disabled={busy || working || !tokenId}
            onClick={revoke}
          >
            {t("common:buttons.revoke")}
          </Button>
        </Stack>
        <Button onClick={onClose}>{t("common:buttons.close")}</Button>
      </DialogActions>
    </Dialog>
  );
}

/** Scans and views over the last 30 days - kept from the old page. */
function Usage({ analytics }: { analytics: GuestAnalyticsSummary }) {
  const { t } = useTranslation("offers");
  const top = (data: Record<string, number>) =>
    Object.entries(data)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  return (
    <Box sx={{ mt: 5 }} data-testid="guest-usage">
      <SubTitle
        title={t("guestCompanionPage.usage.title")}
        subtitle={t("guestCompanionPage.usage.subtitle")}
      />
      <Stack direction="row" spacing={5} flexWrap="wrap" useFlexGap>
        <Metric
          label={t("guestCompanionPage.usage.scans")}
          value={analytics.totals.resolves}
        />
        <Metric
          label={t("guestCompanionPage.usage.pageViews")}
          value={analytics.totals.views}
        />
        <Metric
          label={t("guestCompanionPage.usage.unitsScanned")}
          value={Object.keys(analytics.byRoom).length}
        />
        {(
          [
            [t("guestCompanionPage.usage.topSections"), analytics.sections],
            [t("guestCompanionPage.usage.languages"), analytics.languages],
          ] as [string, Record<string, number>][]
        ).map(([title, data]) => (
          <Box key={title} sx={{ minWidth: 160 }}>
            <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
              {title}
            </Typography>
            {top(data).length === 0 ? (
              <Typography variant="bodySm">
                {t("guestCompanionPage.usage.noData")}
              </Typography>
            ) : (
              top(data).map(([k, v]) => (
                <Stack key={k} direction="row" justifyContent="space-between">
                  <Typography variant="bodySm">{k}</Typography>
                  <Typography variant="bodySm" sx={{ color: tokens.inkMuted }}>
                    {v}
                  </Typography>
                </Stack>
              ))
            )}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <Box>
      <Typography
        sx={{ typography: "headlineLg", fontWeight: "fontWeightSemibold" }}
      >
        {value}
      </Typography>
      <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
        {label}
      </Typography>
    </Box>
  );
}
